import React, {useEffect, useState, useMemo, useRef} from "react";
import {
    LoadingMeter,
    ProviderPatientStatement,
    Badge,
    BadgeType,
    TitleCard,
    DataTablePresenter,
    ITableColumn,
    DataTable,
    renderAmount,
    CurrencyType,
    ProviderProfessionalClaim,
    BaseButton,
    Amount,
    ProviderClaim,
    DatePicker,
} from "@enter/core";
import { formatDate } from '../../services/primitives/date-utils';
import { parseISO, isBefore, isAfter, startOfDay, endOfDay } from 'date-fns';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import _ from "lodash";
import {Fade} from "@mui/material";
import { SortingRule } from 'react-table';
import { TableStorage } from '@enter/core/src/ui/data-table/data-table.service';
import * as S from "./statement-details.styles";
import { RecordPaymentDialog } from "./record-payment-dialog";
import { RecordDiscountDialog } from "./record-discount-dialog";
import { EditPaymentDialog } from "./edit-payment-dialog";
import html2pdf from "html2pdf.js";
import { transformDataForPDF, generateHTML, TEMPLATE_HTML, computeStatementTotals, generateHTMLForStatementChunk } from "./statement-pdf-generator";
import { generateChunkedPdf } from "./statement-chunk-pdf-generator";
import { IStatementDetailsProps } from "./statement-details.types";
import { IPaymentData, createPaymentsColumns } from "./payment-columns";
import { sortPayments } from "./payment-sort";

interface IStatementData {
    id: string; // Unique identifier for each row (used by DataTable)
    claimId?: string; // The actual claim or source ID
    type: 'claim' | 'line' | 'payment' | 'adjustment' | 'allocated' | 'balance' | 'unallocated' | 'summary';
    date?: string;
    description?: string;
    diagnosisCodes?: string[];
    charge?: Amount;
    credit?: Amount;
    balance?: Amount;
    metadata?: {
        pending?: boolean;
        id?: string;
    };
}


const createStatementColumns = (handleEditPayment: (row: IStatementData) => Promise<void> | void): ITableColumn<IStatementData>[] => [
    {
        id: 'date',
        Header: 'Date',
        accessor: 'date',
        disableSortBy: true,
        Cell: ({ cell }): React.ReactElement => <div>{cell.value || ''}</div>,
    },
    {
        id: 'description',
        Header: 'Description',
        accessor: 'description',
        disableSortBy: true,
        Cell: ({ cell }): React.ReactElement => <div>{cell.value || ''}</div>,
    },
    {
        id: 'diagnosisCodes',
        Header: 'Diagnosis codes',
        accessor: 'diagnosisCodes',
        disableSortBy: true,
        Cell: ({ cell }): React.ReactElement => (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {(cell.value || []).map((code: string, idx: number) => (
                    <Badge key={idx} type={BadgeType.BLUE} size="small">{code}</Badge>
                ))}
            </div>
        ),
    },
    {
        id: 'charge',
        Header: 'Charge',
        accessor: 'charge',
        disableSortBy: true,
        Cell: ({ cell }): React.ReactElement => <div>{cell.value ? renderAmount(cell.value) : ''}</div>,
    },
    {
        id: 'credit',
        Header: 'Credit',
        accessor: 'credit',
        disableSortBy: true,
        Cell: ({ cell }): React.ReactElement => <div>{cell.value ? renderAmount(cell.value) : ''}</div>,
    },
    {
        id: 'balance',
        Header: 'Balance',
        accessor: 'balance',
        disableSortBy: true,
        Cell: ({ cell }): React.ReactElement => <div>{cell.value ? renderAmount(cell.value) : ''}</div>,
    },
    {
        id: 'actions',
        Header: 'Actions',
        disableSortBy: true,
        Cell: ({ row }): React.ReactElement => {
            const isAllocatedPayment = row.original.type === 'allocated';
            const isPending = row.original.metadata?.pending;
            const shouldShowButton = isAllocatedPayment && !isPending;

            if (!shouldShowButton) {
                return <div></div>;
            }

            return (
                <BaseButton
                    variant="outlined"
                    onClick={() => {
                        const result = handleEditPayment(row.original);
                        if (result instanceof Promise) {
                            result.catch(err => {
                                console.error('Error in handleEditPayment:', err);
                            });
                        }
                    }}
                >
                    Edit
                </BaseButton>
            );
        },
    },
];

function extractDiagnosisCodes(obj: any): Record<string, string> {
    return Object.entries(obj)
        .filter(([key, value]) => key.includes('DiagnosisCode') && value !== null)
        .reduce((acc, [key, value]) => {
            const match = key.match(/^f21([a-zA-Z])DiagnosisCode$/);
            if (match) {
                const shortKey = match[1];
                acc[shortKey] = value as string;
            }
            return acc;
        }, {} as Record<string, string>);
}

export const StatementDetailsPage: React.FC<IStatementDetailsProps> = (props) => {
    const [patientStatement, setPatientStatement] = useState<ProviderPatientStatement | undefined>(undefined);
    const [professionals, setProfessionals] = useState<ProviderProfessionalClaim[]>([]);
    const [claims, setClaims] = useState<ProviderClaim[] | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);
    const [paymentsSortBy, setPaymentsSortBy] = useState<SortingRule<IPaymentData>[]>([]);
    const [dosFilterFrom, setDosFilterFrom] = useState<Date | null>(null);
    const [dosFilterTo, setDosFilterTo] = useState<Date | null>(null);
    const [dosFilterKey, setDosFilterKey] = useState<number>(0);
    
    const { 
        patientId, 
        patientStore,
        claimStore,
        layoutStore,
        dialogContext, 
        toastContext,
        PageLayout,
        DataTableCard,
        equalsFilter,
        fetchProfessionalClaims,
        fetchClaimsByPatientId,
        claimsColumns,
        getClaimRowHref,
    } = props;

    const handleEditPayment = async (statementRow: IStatementData): Promise<void> => {
        // Ensure patientStatement is loaded before opening dialog
        let statementToUse = patientStatement;
        if (!statementToUse) {
            try {
                setLoading(true);
                statementToUse = await patientStore.loadPatientStatement(patientId);
                setPatientStatement(statementToUse);
            } catch (err) {
                dialogContext.showError(err);
                return;
            } finally {
                setLoading(false);
            }
        }
        
        const handleClose = () => {
            dialogContext.hideDialog();
        };

        const handleSuccess = async () => {
            handleClose();
            setLoading(true);
            try {
                const statement = await patientStore.loadPatientStatement(patientId);
                setPatientStatement(statement);
            } catch (err) {
                dialogContext.showError(err);
            } finally {
                setLoading(false);
            }
        };

        dialogContext.showDialog({
            props: {
                onClose: handleClose,
                disableBackdropClick: true,
                disableEscapeKeyDown: true,
                renderTitle: 'Edit Patient Payment',
            },
            Component: (
                <EditPaymentDialog
                    patientStatement={statementToUse}
                    selectedRow={statementRow}
                    onClose={handleClose}
                    onSuccess={handleSuccess}
                    claimStore={claimStore}
                    dialogContext={dialogContext}
                    toastContext={toastContext}
                />
            ),
        });
    };

    // Process payment data
    const paymentsData = useMemo((): IPaymentData[] => {
        if (!patientStatement) {
            return [];
        }
        
        const statement = patientStatement;

        const paymentSources = statement.paymentSources || [];
        const claims = statement.claims || [];

        return paymentSources.map(paymentSource => {
            const source: IPaymentData = { ...paymentSource };

            let totalRefund = 0;

            for (const claim of claims) {
                const patientPayments = claim.patientPayments || [];

                const refundForSource = patientPayments
                    .filter(patientPayment =>
                        patientPayment.collectionPaymentSourceId === paymentSource.id &&
                        patientPayment.amount &&
                        typeof patientPayment.amount.amount === 'string' &&
                        parseFloat(patientPayment.amount.amount) < 0,
                    )
                    .reduce((sum, patientPayment) => {
                        const value = parseFloat(patientPayment.amount.amount || '0');
                        return Number.isNaN(value) ? sum : sum + value;
                    }, 0);

                totalRefund += refundForSource;
            }

            if (totalRefund !== 0) {
                source.refundAmount = {
                    amount: (totalRefund * -1).toFixed(2),
                    currency: CurrencyType.USD,
                };
            }

            return source;
        });
    }, [patientStatement]);

    // Scripted copied from Retool Patient Statement Details page
    // TODO: Refactor it later
    const statementData = useMemo((): IStatementData[] => {
        if (!patientStatement) {
            return [];
        }
        
        const statement = patientStatement;
        const rows: IStatementData[] = [];

        // Type order for sorting
        const typeOrder: Record<string, number> = {
            'unallocated': 1,
            'claim': 2,
            'line': 3,
            'payment': 4,
            'adjustment': 5,
            'allocated': 6,
            'balance': 7
        };

        // Claims
        for (const claim of statement.claims || []) {
            const row: IStatementData = {
                id: `${claim.id}-claim`,
                claimId: claim.id,
                type: 'claim',
                date: claim.fromDos,
                description: 
                    'Claim: ' + claim.externalId + (claim.pendingAdjudication ? ' (Pending Adjudication) ' : '') + '\n' + 
                    'Provider(s): ' + (claim.renderingProviders || []).map(provider => `${provider.firstName}, ${provider.lastName}`).join(', '),
                metadata: {
                    pending: claim.pendingAdjudication,
                }
            };
            rows.push(row);
        }

        // Claim Payments and Adjustments and Balances
        for (const claim of statement.claims || []) {
            if (claim.payerPayments) {
                for (let paymentIndex = 0; paymentIndex < claim.payerPayments.length; paymentIndex++) {
                    const payment = claim.payerPayments[paymentIndex];
                    const paymentRow: IStatementData = {
                        id: `${claim.id}-payment-${paymentIndex}`,
                        claimId: claim.id,
                        type: 'payment',
                        date: claim.fromDos,
                        description: payment.insuranceName + ' Payment',
                        credit: payment.payerAmount,
                        metadata: {
                            pending: claim.pendingAdjudication,
                        }
                    };
                    rows.push(paymentRow);

                    if (payment.adjustmentAmount) {
                        const adjustmentRow: IStatementData = {
                            id: `${claim.id}-adjustment-${paymentIndex}`,
                            claimId: claim.id,
                            type: 'adjustment',
                            date: claim.fromDos,
                            description: payment.insuranceName + ' Adjustment',
                            credit: payment.adjustmentAmount,
                            metadata: {
                                pending: claim.pendingAdjudication,
                            }
                        };
                        rows.push(adjustmentRow);
                    }
                }
            }

            if (claim.balanceDue) {
                const balanceRow: IStatementData = {
                    id: `${claim.id}-balance`,
                    claimId: claim.id,
                    type: 'balance',
                    date: claim.fromDos,
                    description: 'Your Balance Currently Due On This Claim ...',
                    balance: claim.balanceDue,
                    metadata: {
                        pending: claim.pendingAdjudication,
                    }
                };
                rows.push(balanceRow);
            }

            if (claim.patientPayments) {
                for (const patientPayment of claim.patientPayments) {
                    const allocatedRow: IStatementData = {
                        id: `${claim.id}-allocated-${patientPayment.id}`,
                        claimId: claim.id,
                        type: 'allocated',
                        date: patientPayment.paymentDate,
                        description: 'Patient Payment for Claim ' + claim.externalId,
                        credit: patientPayment.amount,
                        metadata: {
                            id: patientPayment.id,
                            pending: claim.pendingAdjudication,
                        }
                    };
                    rows.push(allocatedRow);
                }
            }

            if (claim.serviceLines) {
                for (const line of claim.serviceLines) {
                    const currentProfessional = _.find(professionals, { 'claimId': claim.id });
                    
                    let diagnosisCodes: string[] = [];
                    if (currentProfessional && currentProfessional.professionalTemplate) {
                        const serviceLineDetails = _.find(currentProfessional.professionalTemplate.f24ServiceLine, { id: line.id });
                        if (serviceLineDetails) {
                            const serviceLinePointers = serviceLineDetails.f24eDiagnosisPointers || [];
                            const diagnosisCodeMap = extractDiagnosisCodes(currentProfessional.professionalTemplate);
                            diagnosisCodes = serviceLinePointers.map((letter: string) => diagnosisCodeMap[letter.toLowerCase()]).filter(Boolean);
                        }
                    }

                    const lineRow: IStatementData = {
                        id: `${claim.id}-line-${line.id}`,
                        claimId: claim.id,
                        type: 'line',
                        date: claim.fromDos,
                        description: line.procedure + ' - ' + line.description,
                        diagnosisCodes,
                        charge: line.chargeAmount,
                        metadata: {
                            pending: claim.pendingAdjudication,
                        }
                    };
                    rows.push(lineRow);
                }
            }
        }

        // Unallocated patient payments
        for (const source of statement.paymentSources || []) {
            if (parseFloat(source.unallocatedAmount?.amount || '0') > 0) {
                const unallocatedRow: IStatementData = {
                    id: `${source.id}-unallocated`,
                    claimId: source.id,
                    type: 'unallocated',
                    date: source.paymentDate,
                    description: 'Patient Payment (Unallocated)',
                    credit: source.unallocatedAmount,
                    balance: {
                        amount: (parseFloat(source.unallocatedAmount?.amount || '0') * -1).toFixed(2),
                        currency: source.unallocatedAmount?.currency || CurrencyType.USD
                    }
                };
                rows.push(unallocatedRow);
            }
        }

        // Custom sort function (mirrors Retool logic: date desc, then id, then type)
        rows.sort((a, b) => {
            // Sort by date descending
            if (a.date !== b.date) {
                return new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime();
            }
            // Sort by claimId
            if (a.claimId !== b.claimId) {
                return (a.claimId || '').localeCompare(b.claimId || '');
            }
            // Sort by type
            return typeOrder[a.type] - typeOrder[b.type];
        });

        return rows;
    }, [patientStatement, professionals]);

    const paymentsColumns = useMemo(() => createPaymentsColumns(), []);
    const statementColumns = useMemo(() => createStatementColumns(handleEditPayment), []);

    const paymentsPresenter = useRef(
        new DataTablePresenter<IPaymentData>({
            name: 'payments',
            data: [],
            columns: paymentsColumns,
            filterSchemas: [],
        })
    ).current;

    const statementPresenter = useRef(
        new DataTablePresenter<IStatementData>({
            name: 'statement',
            data: [],
            columns: statementColumns,
            filterSchemas: [],
        })
    ).current;

    // Claims presenter (only created if columns are provided)
    const claimsPresenter = useRef(
        claimsColumns
            ? new DataTablePresenter<ProviderClaim>({
                  name: 'patient-claims',
                  data: [],
                  columns: claimsColumns,
                  filterSchemas: [],
              })
            : null
    ).current;

    // Apply sorting to payments data (similar to super-dial pattern)
    const sortedPaymentsData = useMemo(() => {
        return sortPayments(paymentsData, paymentsSortBy);
    }, [paymentsData, paymentsSortBy]);

    const isInRange = (dateStr: string | undefined, from: Date | null, to: Date | null): boolean => {
        if (!from && !to) return true;
        if (!dateStr) return true;
        const d = parseISO(dateStr);
        if (from && isBefore(d, startOfDay(from))) return false;
        if (to && isAfter(d, endOfDay(to))) return false;
        return true;
    };

    const filteredStatementData = useMemo(
        () => statementData.filter(r => isInRange(r.date, dosFilterFrom, dosFilterTo)),
        [statementData, dosFilterFrom, dosFilterTo],
    );

    const filteredPaymentsData = useMemo(() => {
        if (!dosFilterFrom && !dosFilterTo) return sortedPaymentsData;
        const statementClaims = patientStatement?.claims || [];
        return sortedPaymentsData.filter(p => {
            const associatedClaims = statementClaims.filter(c =>
                (c.patientPayments || []).some(pp => pp.collectionPaymentSourceId === p.id),
            );

            if (associatedClaims.length === 0) return true;
            return associatedClaims.some(c => isInRange(c.fromDos, dosFilterFrom, dosFilterTo));
        });
    }, [sortedPaymentsData, dosFilterFrom, dosFilterTo, patientStatement]);

    const filteredClaims = useMemo(
        () => (claims || [])
            .filter(c => isInRange(c.fromDos, dosFilterFrom, dosFilterTo))
            .sort((a, b) => {
                const aTime = a.fromDos ? new Date(a.fromDos).getTime() : 0;
                const bTime = b.fromDos ? new Date(b.fromDos).getTime() : 0;
                return bTime - aTime;
            }),
        [claims, dosFilterFrom, dosFilterTo],
    );

    const filteredBalance = useMemo(() => {
        if (!dosFilterFrom && !dosFilterTo) {
            return patientStatement?.balanceDue?.amount || '0.00';
        }
        const total = filteredStatementData.reduce(
            (sum, r) => sum + parseFloat(r.balance?.amount || '0'),
            0,
        );
        return total.toFixed(2);
    }, [filteredStatementData, dosFilterFrom, dosFilterTo, patientStatement]);

    // Watch TableStorage for sort changes (DataTable saves sort state to localStorage)
    // Since localStorage is not observable, we check periodically
    useEffect(() => {
        const storageKey = paymentsPresenter.storageKey;
        
        // Check sort state immediately
        const checkSortState = () => {
            const defaults = TableStorage.getItem<{ sortBy?: SortingRule<IPaymentData>[] }>(storageKey, 'defaults');
            const currentSortBy = defaults?.sortBy || [];
            
            // Only update if sortBy has changed
            setPaymentsSortBy(prevSortBy => {
                if (JSON.stringify(prevSortBy) !== JSON.stringify(currentSortBy)) {
                    return currentSortBy as SortingRule<IPaymentData>[];
                }
                return prevSortBy;
            });
        };
        
        // Check immediately
        checkSortState();
        
        // Check periodically (every 100ms) to catch sort changes
        const interval = setInterval(checkSortState, 100);
        
        return () => {
            clearInterval(interval);
        };
    }, [paymentsPresenter]);

    // Update presenter when sorted/filtered data changes
    useEffect(() => {
        paymentsPresenter.onDataUpdate(filteredPaymentsData);
        paymentsPresenter.onDataTotalChange(filteredPaymentsData.length);
    }, [filteredPaymentsData, paymentsPresenter]);

    useEffect(() => {
        statementPresenter.onDataUpdate(filteredStatementData);
        statementPresenter.onDataTotalChange(filteredStatementData.length);
    }, [filteredStatementData, statementPresenter]);

    // Update claims presenter when claims data changes
    useEffect(() => {
        if (claimsPresenter) {
            claimsPresenter.onDataUpdate(filteredClaims);
            claimsPresenter.onDataTotalChange(filteredClaims.length);
        }
    }, [filteredClaims, claimsPresenter]);

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchData();
    }, [patientId]);

    const fetchData = async (): Promise<void> => {
        if (!patientStatement) {
            try {
                setLoading(true);
                const statement = await patientStore.loadPatientStatement(patientId);
                setPatientStatement(statement);
            } catch (err) {
                dialogContext.showError(err);
            } finally {
                setLoading(false);
            }
        }
    };

    // Fetch professional claims data when statement is loaded
    useEffect(() => {
        const fetchProfessionals = async () => {
            if (patientStatement && patientStatement.claims && patientStatement.claims.length > 0) {
                try {
                    const claimIds = patientStatement.claims.map(claim => claim.id);
                    const professionalClaims = await fetchProfessionalClaims(claimIds);
                    setProfessionals(professionalClaims || []);
                } catch (err) {
                    console.error('Error fetching professional claims:', err);
                    setProfessionals([]);
                }
            }
        };
        fetchProfessionals();
    }, [patientStatement, fetchProfessionalClaims]);

    // Fetch claims by patient ID
    useEffect(() => {
        let isMounted = true;
        
        const fetchClaims = async () => {
            if (patientId && fetchClaimsByPatientId) {
                try {
                    const patientClaims = await fetchClaimsByPatientId(patientId);
                    if (isMounted) {
                        setClaims(patientClaims || []);
                    }
                } catch (err) {
                    console.error('Error fetching claims:', err);
                    if (isMounted) {
                        setClaims([]);
                    }
                }
            }
        };
        fetchClaims();
        
        return () => {
            isMounted = false;
        };
    }, [patientId, fetchClaimsByPatientId]);

    const renderLoader = (): React.ReactElement => (
        <Fade in={true}>
            <S.Loader>
                <LoadingMeter />
            </S.Loader>
        </Fade>
    );

    const formatPhoneNumber = (phone?: string): string => {
        if (!phone) return '';
        return phone;
    };

    const formatAddress = (addresses?: any[]): string => {
        if (!addresses || addresses.length === 0) return '';
        const address = addresses[0];
        const lines = address.lines?.join(', ') || '';
        const cityStateZip = `${address.city || ''}, ${address.state || ''} ${address.postal || ''}`.trim();
        return `${lines}${lines && cityStateZip ? ', ' : ''}${cityStateZip}`;
    };

    const handleRecordPayment = (): void => {
        const handleClose = () => {
            dialogContext.hideDialog();
        };

        const handleSuccess = async () => {
            handleClose();
            // Reload the patient statement data
            setLoading(true);
            try {
                const statement = await patientStore.loadPatientStatement(patientId);
                setPatientStatement(statement);
            } catch (err) {
                dialogContext.showError(err);
            } finally {
                setLoading(false);
            }
        };

        dialogContext.showDialog({
            props: {
                onClose: handleClose,
                disableBackdropClick: true,
                disableEscapeKeyDown: true,
                renderTitle: 'Record Payment',
            },
            Component: (
                <RecordPaymentDialog
                    patientStatement={patientStatement}
                    onClose={handleClose}
                    onSuccess={handleSuccess}
                    claimStore={claimStore}
                    dialogContext={dialogContext}
                    toastContext={toastContext}
                />
            ),
        });
    };

    const handleRecordDiscount = (): void => {
        const handleClose = () => {
            dialogContext.hideDialog();
        };

        const handleSuccess = async () => {
            handleClose();
            // Reload the patient statement data
            setLoading(true);
            try {
                const statement = await patientStore.loadPatientStatement(patientId);
                setPatientStatement(statement);
            } catch (err) {
                dialogContext.showError(err);
            } finally {
                setLoading(false);
            }
        };

        dialogContext.showDialog({
            props: {
                onClose: handleClose,
                disableBackdropClick: true,
                disableEscapeKeyDown: true,
                renderTitle: 'Record Discount',
            },
            Component: (
                <RecordDiscountDialog
                    patientStatement={patientStatement}
                    onClose={handleClose}
                    onSuccess={handleSuccess}
                    dialogContext={dialogContext}
                    toastContext={toastContext}
                    equalsFilter={equalsFilter}
                />
            ),
        });
    };

    const handleCopyClaimUrl = async (): Promise<void> => {
        const url = window.location.href;
        
        try {
            await navigator.clipboard.writeText(url);
            toastContext.notify?.('URL copied to clipboard', 'left');
        } catch (err) {
            console.error('Failed to copy URL:', err);
            toastContext.notify?.('Failed to copy URL to clipboard', 'left', 'warning');
        }
    };

    const handlePrintPDF = async (): Promise<void> => {
        if (!patientStatement) {
            toastContext.notify?.('Patient statement is not loaded', 'left', 'warning');
            return;
        }

        setIsGeneratingPdf(true);

        try {
            // Transform data for PDF — use filtered data and override balance to match filtered view
            const pdfStatement = {
                ...patientStatement,
                balanceDue: { amount: filteredBalance, currency: 'USD' },
            };
            const patientDetails = transformDataForPDF(pdfStatement, filteredPaymentsData, filteredStatementData, filteredClaims);

            // Generate filename from patient name and date/time
            const firstName = _.startCase(_.lowerCase(patientStatement.patient?.first || ''));
            const middleName = _.startCase(_.lowerCase(patientStatement.patient?.middle || ''));
            const lastName = _.startCase(_.lowerCase(patientStatement.patient?.last || ''));
            const fullName = `${firstName} ${middleName} ${lastName}`.trim();
            const date = new Date().toLocaleDateString();
            const time = new Date().toLocaleTimeString();
            const fileName = `${fullName} - ${date} ${time}.pdf`;

            const CHUNKING_ROW_THRESHOLD = 100;
            const totalStatementRows = patientDetails.statementsList?.length ?? 0;

            if (totalStatementRows > CHUNKING_ROW_THRESHOLD) {
                const totals = computeStatementTotals(patientDetails.statementsList || []);

                await generateChunkedPdf({
                    baseData: patientDetails,
                    makeHtml: (data, info) =>
                        generateHTMLForStatementChunk(data, TEMPLATE_HTML, {
                            showTotalRow: info.chunkIndex === info.totalChunks - 1,
                            totals,
                            // Only show the "Statement" card title on the first chunk.
                            showTitle: info.chunkIndex === 0,
                        }),
                    filename: fileName,
                    rowsPerChunk: 100,
                    headerOnlyOnFirstChunk: true,
                    jsPdf: {
                        unit: 'in',
                        format: 'letter',
                        orientation: 'landscape',
                        margin: [0.1, 0.1, 0.1, 0.1],
                    },
                    html2canvas: {
                        canvasScale: 1.5,
                        backgroundColor: '#f5f7fb',
                        useCORS: true,
                    },
                });

                toastContext.notify?.('PDF generated successfully (chunked)', 'left', 'light');
            } else {
                // Standard (single-shot) PDF generation for smaller statements
                const htmlContent = generateHTML(patientDetails, TEMPLATE_HTML);

                const element = document.createElement('div');
                element.innerHTML = htmlContent;
                element.style.width = '100%';
                element.style.margin = '0';
                element.style.padding = '0';
                element.style.fontFamily = 'Arial, sans-serif';
                
                const opt = {
                    margin: [0.1, 0.1, 0.1, 0.1] as [number, number, number, number],
                    filename: fileName,
                    image: { type: 'jpeg' as const, quality: 0.98 },
                    html2canvas: {
                        scale: 1.5,
                        useCORS: true,
                        allowTaint: true,
                        backgroundColor: '#f5f7fb',
                        scrollX: 0,
                        scrollY: 0,
                        logging: false
                    },
                    jsPDF: {
                        unit: 'in',
                        format: 'letter',
                        orientation: 'landscape' as const,
                        compress: true,
                        putOnlyUsedFonts: true,
                        floatPrecision: 16
                    },
                    pagebreak: {
                        mode: ['css', 'legacy'],
                        before: '.page-break-before',
                        after: '.page-break-after',
                        avoid: ['.avoid-break', '.table tr', '.section-title']
                    }
                };
                
                await html2pdf().set(opt).from(element).save();
                toastContext.notify?.('PDF generated successfully', 'left', 'light');
            }
        } catch (error) {
            console.error('Error generating PDF:', error);
            toastContext.notify?.('Error generating PDF. Please check the console for details.', 'left', 'warning');
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const renderPage = (statement: ProviderPatientStatement): React.ReactElement => {
        const patient = statement.patient;
        const billingProvider = statement.billingProvider;
        const balance = filteredBalance;

        return (
            <PageLayout>
                <S.HeaderRow>
                    <S.PageTitle>Patient Details</S.PageTitle>
                    <S.ButtonGroup>
                        <BaseButton variant="primary" onClick={handleCopyClaimUrl}>
                            Copy URL
                        </BaseButton>
                        <BaseButton 
                            variant="primary" 
                            onClick={handlePrintPDF}
                            disabled={isGeneratingPdf}
                        >
                            {isGeneratingPdf ? 'Generating PDF...' : 'Print'}
                        </BaseButton>
                    </S.ButtonGroup>
                </S.HeaderRow>

                <S.BalanceCard>
                    <TitleCard size="large" title="Current Balance">
                        <S.BalanceContent>
                            <S.BalanceAmount>${balance}</S.BalanceAmount>
                        </S.BalanceContent>
                    </TitleCard>
                </S.BalanceCard>
                <S.CardsRow>
                    <S.HalfWidthCard>
                        <TitleCard size="large" title="Patient">
                            <S.CardContent>
                                <S.PatientName>
                                    {patient?.first} {patient?.middle} {patient?.last} {patient?.suffix}
                                    {patient?.sex && (
                                        <S.SexBadge>
                                            <Badge type={BadgeType.BLUE} size="small">{patient.sex}</Badge>
                                        </S.SexBadge>
                                    )}
                                </S.PatientName>
                                <S.InfoRow>
                                    <S.InfoLabel>DOB</S.InfoLabel>
                                    <S.InfoValue>{patient?.dob ? formatDate(patient.dob, 'MMM dd, yyyy') : ''}</S.InfoValue>
                                </S.InfoRow>
                                <S.InfoRow>
                                    <S.InfoLabel>External ID</S.InfoLabel>
                                    <S.InfoValue>{patient?.externalId || ''}</S.InfoValue>
                                </S.InfoRow>
                                <S.InfoRow>
                                    <S.InfoLabel>Phone</S.InfoLabel>
                                    <S.InfoValue>{formatPhoneNumber(patient?.phones?.[0])}</S.InfoValue>
                                </S.InfoRow>
                                <S.InfoRow>
                                    <S.InfoLabel>Email</S.InfoLabel>
                                    <S.InfoValue>
                                        <S.EmailLink>{patient?.emails?.[0] || ''}</S.EmailLink>
                                    </S.InfoValue>
                                </S.InfoRow>
                                <S.InfoRow>
                                    <S.InfoLabel>Address</S.InfoLabel>
                                    <S.InfoValue>{formatAddress(patient?.addresses)}</S.InfoValue>
                                </S.InfoRow>
                            </S.CardContent>
                        </TitleCard>
                    </S.HalfWidthCard>

                    <S.HalfWidthCard>
                        <TitleCard size="large" title="Billing Provider">
                            <S.CardContent>
                                <S.ProviderName>
                                    {billingProvider?.organization || 
                                     `${billingProvider?.first || ''} ${billingProvider?.middle || ''} ${billingProvider?.last || ''}`.trim()}
                                </S.ProviderName>
                                <S.InfoRow>
                                    <S.InfoLabel>Phone</S.InfoLabel>
                                    <S.InfoValue>{formatPhoneNumber(billingProvider?.phone)}</S.InfoValue>
                                </S.InfoRow>
                                <S.InfoRow>
                                    <S.InfoLabel>Email</S.InfoLabel>
                                    <S.InfoValue>
                                        <S.EmailLink>{billingProvider?.email || ''}</S.EmailLink>
                                    </S.InfoValue>
                                </S.InfoRow>
                                <S.InfoRow>
                                    <S.InfoLabel>Address</S.InfoLabel>
                                    <S.InfoValue>{formatAddress(billingProvider?.addresses)}</S.InfoValue>
                                </S.InfoRow>
                            </S.CardContent>
                        </TitleCard>
                    </S.HalfWidthCard>
                </S.CardsRow>

                <S.FilterRow>
                    <S.FilterLabel>Date of Service</S.FilterLabel>
                    <DatePicker
                        key={`dos-from-${dosFilterKey}`}
                        value={dosFilterFrom ?? undefined}
                        onDateSelected={(d) => setDosFilterFrom(d ?? null)}
                    />
                    <S.FilterSeparator>–</S.FilterSeparator>
                    <DatePicker
                        key={`dos-to-${dosFilterKey}`}
                        value={dosFilterTo ?? undefined}
                        onDateSelected={(d) => setDosFilterTo(d ?? null)}
                    />
                    <S.ClearFilterButton
                        onClick={() => { setDosFilterFrom(null); setDosFilterTo(null); setDosFilterKey(k => k + 1); }}
                        title="Clear date filter"
                    >
                        <Icon path={mdiClose} size={0.75} />
                    </S.ClearFilterButton>
                </S.FilterRow>

                {claimsColumns && claimsPresenter && (
                    <S.TableCardWrapper>
                        <S.TableCardTitle>Claims</S.TableCardTitle>
                        <DataTableCard size="medium">
                            <S.ClaimsTable
                                presenter={claimsPresenter}
                                isLoading={claims === undefined}
                                disableSorting={true}
                                showExport={true}
                                hideFiltering={true}
                                hidePagination={true}
                                getRowHref={getClaimRowHref ? (row) => getClaimRowHref(row.original) : undefined}
                            />
                        </DataTableCard>
                    </S.TableCardWrapper>
                )}

                <S.TableCardWrapper>
                    <S.TableHeaderRow>
                        <S.TableCardTitle style={{ padding: 0 }}>Payments</S.TableCardTitle>
                        <S.ButtonGroup>
                            <BaseButton variant="primary" onClick={handleRecordPayment}>
                                Record payment
                            </BaseButton>
                            <BaseButton variant="primary" onClick={handleRecordDiscount}>
                                Record discount
                            </BaseButton>
                        </S.ButtonGroup>
                    </S.TableHeaderRow>
                    <DataTableCard size="medium">
                        <DataTable
                            presenter={paymentsPresenter}
                            hideFiltering={true}
                            showExport={true}
                            hidePagination={true}
                            isLoading={loading}
                        />
                    </DataTableCard>
                </S.TableCardWrapper>

                <S.TableCardWrapper>
                    <S.TableCardTitle>Statement</S.TableCardTitle>
                    <DataTableCard size="medium">
                        <DataTable
                            presenter={statementPresenter}
                            hideFiltering={true}
                            hidePagination={true}
                            isLoading={loading}
                            showExport={true}
                            rowClass={(row) => row.original.metadata?.pending ? 'pending-row' : ''}
                            summaryColumns={['charge', 'credit', 'balance']}
                        />
                    </DataTableCard>
                </S.TableCardWrapper>
            </PageLayout>
        );
    };

    return patientStatement ? renderPage(patientStatement) : renderLoader();
};


